import React, { useEffect, useState } from "react";
import { X, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * AdminRecordModal — opens any admin-fetchable record, fetches its full
 * document, and renders every key/value as a clean two-column table.
 * Drives the "View details" link on every dashboard row.
 *
 * `collection` must be one of the whitelisted names on the backend
 * (host_applications, concierge_requests, add_on_requests, itineraries,
 *  itinerary_offer_claims, users, marketing_consents, accommodations,
 *  guest_contacts, jewel_store_bookings, booking_requests).
 */
export default function AdminRecordModal({ open, onClose, collection, recordId, title }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open || !recordId || !collection) return;
    setLoading(true);
    setErr(null);
    setDoc(null);
    api
      .get(`/admin/records/${collection}/${recordId}`)
      .then((r) => setDoc(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load record."))
      .finally(() => setLoading(false));
  }, [open, recordId, collection]);

  if (!open) return null;

  const fmtValue = (v) => {
    if (v === null || v === undefined || v === "") return "—";
    if (Array.isArray(v)) {
      if (!v.length) return "—";
      // Array of strings → comma-list. Array of objects → JSON pretty-print.
      if (v.every((x) => typeof x !== "object")) return v.join(", ");
      return <pre className="text-[11px] whitespace-pre-wrap break-words bg-[var(--ivory-2)] p-2 -m-1 rounded">{JSON.stringify(v, null, 2)}</pre>;
    }
    if (typeof v === "object") {
      return <pre className="text-[11px] whitespace-pre-wrap break-words bg-[var(--ivory-2)] p-2 -m-1 rounded">{JSON.stringify(v, null, 2)}</pre>;
    }
    if (typeof v === "boolean") return v ? "Yes" : "No";
    if (typeof v === "string" && /^https?:\/\//i.test(v)) {
      return (
        <a href={v} target="_blank" rel="noreferrer" className="text-[var(--gold)] hover:underline inline-flex items-center gap-1 break-all">
          {v} <ExternalLink size={11} strokeWidth={1.5} />
        </a>
      );
    }
    if (typeof v === "string" && /^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(v)) {
      return <a href={`mailto:${v}`} className="text-[var(--gold)] hover:underline">{v}</a>;
    }
    if (typeof v === "string" && /^\+?[\d\s().-]{7,}$/.test(v) && /\d{4,}/.test(v)) {
      return <a href={`tel:${v.replace(/[^\d+]/g, "")}`} className="text-[var(--gold)] hover:underline">{v}</a>;
    }
    return String(v);
  };

  // Sort keys: id-ish + name-ish + email first, then created_at last.
  const orderKeys = (obj) => {
    if (!obj) return [];
    const priority = [
      "application_number", "confirmation_number", "recall_code", "id",
      "status", "name", "email", "phone",
    ];
    const trailing = ["created_at", "approved_at", "declined_at", "updated_at"];
    const all = Object.keys(obj);
    const head = priority.filter((k) => all.includes(k));
    const tail = trailing.filter((k) => all.includes(k));
    const middle = all.filter((k) => !head.includes(k) && !tail.includes(k)).sort();
    return [...head, ...middle, ...tail];
  };

  const copyAll = () => {
    if (!doc) return;
    const text = orderKeys(doc).map((k) => `${k}: ${typeof doc[k] === "object" ? JSON.stringify(doc[k]) : doc[k]}`).join("\n");
    try {
      navigator.clipboard?.writeText(text);
      toast.success("Record copied.");
    } catch (_) {
      /* ignore */
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[var(--emerald)]/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="admin-record-modal"
      onClick={onClose}
    >
      <div
        className="bg-[var(--ivory)] max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[var(--ivory)] border-b border-[var(--emerald)]/10 px-8 py-5 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="overline text-[var(--brand-red)]">Record</div>
            <div className="font-display text-2xl text-[var(--emerald)] mt-1 truncate">
              {title || collection}
            </div>
            <div className="text-[11px] text-[var(--muted)] mt-0.5 truncate" data-testid="admin-record-id">{recordId}</div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={copyAll}
              className="text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 border border-[var(--gold)]/40 text-[var(--emerald)] hover:bg-[var(--gold)] hover:text-[var(--ivory)] transition-colors"
              data-testid="admin-record-copy"
            >
              <Copy size={11} strokeWidth={1.5} className="inline mr-1.5 -mt-0.5" /> Copy
            </button>
            <button
              onClick={onClose}
              aria-label="Close"
              className="text-[var(--emerald)] hover:text-[var(--brand-red)]"
              data-testid="admin-record-close"
            >
              <X size={20} strokeWidth={1.5} />
            </button>
          </div>
        </div>

        <div className="p-8">
          {loading && <div className="text-sm text-[var(--muted)]">Loading…</div>}
          {err && <div className="text-sm text-[var(--brand-red)]">{err}</div>}
          {doc && (
            <dl className="divide-y divide-[var(--emerald)]/10" data-testid="admin-record-fields">
              {orderKeys(doc).map((k) => (
                <div key={k} className="grid sm:grid-cols-[180px_1fr] gap-4 py-3">
                  <dt className="overline text-[var(--emerald)]">{k.replace(/_/g, " ")}</dt>
                  <dd className="text-sm text-[var(--ink-2)] break-words" data-testid={`admin-record-field-${k}`}>
                    {fmtValue(doc[k])}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </div>
    </div>
  );
}
